import { Component, inject, input, OnChanges, QueryList, signal, SimpleChanges, ViewChildren } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TeamsAutoAttendantsService } from '../../../teams-auto-attendants.service';
import { MenuButtonFormComponent, MenuButtonRowEntity } from '../menu-button-form/menu-button-form.component';
import { NgClass } from '@angular/common';
import { TeamsAutoAttendantEditContext } from '../../../teams-auto-attendant-edit.context';
import { UiCallFlow } from '../call-flows-side-nav/call-flows-side-nav.component';
import { isEqual } from 'lodash';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ButtonSizes, ButtonStyles } from '../../../../button/button.component';
import {
  AutoAttendantButton,
  AutoAttendantButtonAction,
  AutoAttendantButtonView,
  DialKeys,
} from '../../../../shared/models/generated/ziroModels';
import { ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { ZiroFormsModule } from '../../../../forms/ziro-forms.module';

export interface UiDialButton extends AutoAttendantButtonView {
  key: string;
  prop: keyof DialKeys;
  label: string;
  icon: string;
  audioFile?: File;
  subLabel?: string;
  audioFileId?: string | null;
}

const ALL_BUTTON_DEFS: Array<{ prop: keyof DialKeys; key: string }> = [
  { prop: 'button1', key: '1' },
  { prop: 'button2', key: '2' },
  { prop: 'button3', key: '3' },
  { prop: 'button4', key: '4' },
  { prop: 'button5', key: '5' },
  { prop: 'button6', key: '6' },
  { prop: 'button7', key: '7' },
  { prop: 'button8', key: '8' },
  { prop: 'button9', key: '9' },
  { prop: 'button0', key: '0' },
  { prop: 'buttonStar', key: '*' },
  { prop: 'buttonPound', key: '#' },
];

function isConfigured(button: AutoAttendantButton): boolean {
  return !!button && !!button.action;
}

@Component({
  selector: 'ziro-auto-attendants-menu-buttons',
  imports: [TranslateModule, MenuButtonFormComponent, NgClass, ZiroFormsModule],
  templateUrl: './teams-auto-attendants-menu-buttons.component.html',
  styleUrl: './teams-auto-attendants-menu-buttons.component.scss',
  providers: [TeamsAutoAttendantsService],
})
export class TeamsAutoAttendantsMenuButtonsComponent implements OnChanges {
  private _editContext = inject(TeamsAutoAttendantEditContext);
  private _teamsAutoAttendantService = inject(TeamsAutoAttendantsService);

  @ViewChildren(MenuButtonFormComponent) rowForms!: QueryList<MenuButtonFormComponent>;

  callFlow = input<UiCallFlow>(null);
  tenantId = input.required<string>();
  dialKeys = input<Partial<DialKeys>>({});
  buttons = signal<UiDialButton[]>([]);
  unusedOpen = signal(false);

  private _expandedKey = signal<string | null>(null);
  private _buttonValidity = signal<Record<string, ZiroFormsValidationState>>({});
  private _trackedFlowId: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    const flowId = this.callFlow()?.id ?? null;
    if (flowId !== this._trackedFlowId) {
      this._trackedFlowId = flowId;
      this._buttonValidity.set({});
    }
    if (changes['dialKeys']) {
      this._syncButtons();
    }
  }

  get configuredButtons(): UiDialButton[] {
    return this.buttons().filter((dialButton) => dialButton.action);
  }

  get unusedKeys(): { prop: string; key: string }[] {
    const configuredKeys = new Set(this.configuredButtons.map((b) => b.key));
    return ALL_BUTTON_DEFS.filter(({ key }) => !configuredKeys.has(key));
  }

  isExpanded(key: string): boolean {
    return this._expandedKey() === key;
  }

  toggleRow(key: string): void {
    this._expandedKey.set(this._expandedKey() === key ? null : key);
  }

  toggleUnused(): void {
    this.unusedOpen.set(!this.unusedOpen());
  }

  onRowUpdate(update: ZiroFormsUpdate<MenuButtonRowEntity>): void {
    this._updateButtonValidity(update);
    if (!this._shouldUpdateButton(update)) {
      return;
    }

    const resolvedAudioFile = this._resolveAudioFile(update.new);
    this.buttons.update((current) => this._updateButtons(current, update.new, resolvedAudioFile));

    const flowId = this.callFlow()?.id;
    if (flowId) {
      this._editContext.updateDialKeys(flowId, this.buttons());
    }
  }

  isValid(): boolean {
    const prompt = this.callFlow().playMenuOptionSettings.prompt;
    return !prompt || Object.values(this._buttonValidity()).every((state) => state === ZiroFormsValidationState.VALID);
  }

  hasError(key: string): boolean {
    const state = this._buttonValidity()[key];
    return state !== undefined && state !== ZiroFormsValidationState.VALID;
  }

  get hasAnyUnusedError(): boolean {
    return this.unusedKeys.some(({ key }) => this.hasError(key));
  }

  validateAndShowErrors(): boolean {
    const invalidEntries = Object.entries(this._buttonValidity()).filter(
      ([, state]) => state !== ZiroFormsValidationState.VALID,
    );

    if (invalidEntries.length === 0) {
      this.rowForms?.forEach((form) => form.triggerSubmitValidation());
      return this.rowForms?.toArray().every((form) => form.isFormValid()) ?? true;
    }

    this._expandAndFocusInvalidKey(invalidEntries[0][0]);
    return false;
  }

  private _expandAndFocusInvalidKey(invalidKey: string): void {
    if (this.unusedKeys.some(({ key }) => key === invalidKey)) {
      this.unusedOpen.set(true);
    }
    this._expandedKey.set(invalidKey);
    setTimeout(() => {
      this.rowForms?.find((form) => form.buttonKey() === invalidKey)?.triggerSubmitValidation();
    });
  }

  private _syncButtons(): void {
    const keys = this.dialKeys() ?? {};
    const configured = ALL_BUTTON_DEFS.filter(({ prop }) => isConfigured((keys as any)[prop])).map(({ prop, key }) => {
      const btn = (keys as any)[prop] as AutoAttendantButtonView;
      const raw: UiDialButton = {
        key,
        prop,
        action: btn.action,
        destination: btn.destination ?? '',
        audioFileId:
          btn.action === AutoAttendantButtonAction.ANNOUNCEMENT_AUDIO_FILE ? (btn.destination ?? null) : null,
        label: '',
        icon: '',
        entity: btn.entity ?? null,
        audioInfo: btn.audioInfo ?? null,
        voiceCommands: btn.voiceCommands ?? [],
      };
      return this._withResolvedDisplay(raw);
    });

    this.buttons.set(configured);
  }

  private _updateButtonValidity(update: ZiroFormsUpdate<MenuButtonRowEntity>): void {
    this._buttonValidity.update((current) => ({
      ...current,
      [update.new.key]: update.valid ?? ZiroFormsValidationState.VALID,
    }));
  }

  private _shouldUpdateButton(update: ZiroFormsUpdate<MenuButtonRowEntity>): boolean {
    return !!update.new && !!update.old && !isEqual(update.old, update.new);
  }

  private _resolveAudioFile(update: MenuButtonRowEntity): File | null {
    const existingButton = this.buttons().find((button) => button.key === update.key);

    if (update.audioFile instanceof File) {
      return update.audioFile;
    }

    return existingButton?.audioFile instanceof File ? existingButton.audioFile : null;
  }

  private _updateButtons(
    current: UiDialButton[],
    update: MenuButtonRowEntity,
    resolvedAudioFile: File | null,
  ): UiDialButton[] {
    const existing = current.find((button) => button.key === update.key);

    if (existing) {
      return current.map((button) =>
        button.key === update.key ? this._mergeExistingButton(button, update, resolvedAudioFile) : button,
      );
    }

    const promotedButton = this._createPromotedButton(update, resolvedAudioFile);
    return promotedButton ? [...current, promotedButton] : current;
  }

  private _mergeExistingButton(
    button: UiDialButton,
    update: MenuButtonRowEntity,
    resolvedAudioFile: File | null,
  ): UiDialButton {
    const isDoNothing = update.action === null;
    const merged: UiDialButton = isDoNothing
      ? { ...button, action: null, destination: '', entity: null, audioFile: null, audioInfo: null, audioFileId: null }
      : {
          ...button,
          action: update.action,
          destination: update.destination,
          entity: update.entity ?? null,
          audioFile: resolvedAudioFile,
          audioInfo: button.audioInfo,
          audioFileId: resolvedAudioFile instanceof File ? null : (button.audioFileId ?? null),
        };

    return this._withResolvedDisplay(merged);
  }

  private _createPromotedButton(update: MenuButtonRowEntity, resolvedAudioFile: File | null): UiDialButton | null {
    const def = ALL_BUTTON_DEFS.find((buttonDef) => buttonDef.key === update.key);
    if (!def || !update.action) {
      return null;
    }

    const promoted: UiDialButton = {
      key: def.key,
      prop: def.prop,
      action: update.action,
      destination: update.destination ?? '',
      audioFileId: null,
      label: '',
      icon: '',
      entity: update.entity ?? null,
      audioInfo: null,
      voiceCommands: [],
      audioFile: resolvedAudioFile,
    };

    return this._withResolvedDisplay(promoted);
  }

  private _withResolvedDisplay(button: UiDialButton): UiDialButton {
    const display = this._teamsAutoAttendantService.resolveButtonDisplay(button);
    return {
      ...button,
      label: display.label,
      destination: display.destination,
      icon: display.icon,
      subLabel: display.subLabel,
    };
  }

  protected readonly ZiroIcons = ZiroIcons;
  protected readonly ButtonSizes = ButtonSizes;
  protected readonly ButtonStyles = ButtonStyles;
  protected readonly AutoAttendantButtonAction = AutoAttendantButtonAction;
}
